'use client';

import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { INTAKE_TREND, DEPT_RISK_DATA, LIFECYCLE_FUNNEL } from '@/lib/mockData';

// Custom tooltip wrapper
function TooltipCard({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card p-2.5 shadow-md text-xs min-w-[120px]">
      <p className="font-semibold text-[hsl(var(--text-primary))] mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={`tp-${i}`} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 text-[hsl(var(--text-muted))]">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-[hsl(var(--text-primary))] tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardCharts() {
  const intakeDateRange = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = new Date(end.getFullYear(), end.getMonth() - 5, 1);
    const fmt = (d: Date) =>
      d.toLocaleString('default', { month: 'short', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Intake trend — spans 2 cols */}
      <div className="lg:col-span-2 card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Vendor Intake Activity</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">Submissions, approvals, and rejections · {intakeDateRange}</p>
          </div>
          <button className="btn-ghost text-xs">Export</button>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={INTAKE_TREND} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradSubmitted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0284c7" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#0284c7" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 88%)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<TooltipCard />} />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
              iconType="circle"
              iconSize={8}
            />
            <Area type="monotone" dataKey="submitted" name="Submitted" stroke="#0d9488" strokeWidth={1.5} fill="url(#gradSubmitted)" />
            <Area type="monotone" dataKey="approved" name="Approved" stroke="#0284c7" strokeWidth={1.5} fill="url(#gradApproved)" />
            <Area type="monotone" dataKey="rejected" name="Rejected" stroke="#dc2626" strokeWidth={1.5} fill="none" strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Lifecycle stage funnel */}
      <div className="card p-4">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Vendor Lifecycle Distribution</h2>
          <p className="text-xs text-[hsl(var(--text-muted))]">Current stage counts</p>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={LIFECYCLE_FUNNEL}
            layout="vertical"
            margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 88%)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="stage" type="category" tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} width={80} />
            <Tooltip content={<TooltipCard />} />
            <Bar dataKey="count" name="Vendors" radius={[0, 3, 3, 0]}>
              {LIFECYCLE_FUNNEL.map((entry, index) => (
                <Cell key={`lf-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Dept risk distribution — full width */}
      <div className="lg:col-span-3 card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Risk Distribution by Department</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">Vendor count by risk tier per city department</p>
          </div>
          <button className="btn-ghost text-xs">View Dept Report</button>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={DEPT_RISK_DATA} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 88%)" vertical={false} />
            <XAxis dataKey="dept" tick={{ fontSize: 10, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<TooltipCard />} />
            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} iconType="square" iconSize={8} />
            <Bar dataKey="critical" name="Critical" stackId="a" fill="#dc2626" radius={[0, 0, 0, 0]} />
            <Bar dataKey="high" name="High" stackId="a" fill="#f97316" />
            <Bar dataKey="moderate" name="Moderate" stackId="a" fill="#d97706" />
            <Bar dataKey="low" name="Low" stackId="a" fill="#6b7280" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}